/**
 * Payment Refunds Hooks
 * 
 * Manages the complete payment refund workflow from request through approval
 * to processing. Supports both full and partial refunds with admin approval
 * requirements, reason tracking, and Stripe integration.
 * 
 * **Refund Workflow:**
 * 1. **Request** - User/client requests refund with reason
 * 2. **Review** - Admin reviews request and supporting details
 * 3. **Approval/Rejection** - Admin makes decision with notes
 * 4. **Processing** - Approved refunds sent to Stripe
 * 5. **Completion** - Stripe webhook confirms refund
 * 
 * **Refund Types:**
 * - **Full Refund**: Complete payment reversal
 * - **Partial Refund**: Specified amount less than original payment
 * 
 * **Refund Status Flow:**
 * - `requested`: Initial request submitted
 * - `approved`: Admin approved, pending Stripe processing
 * - `processing`: Refund being processed by Stripe
 * - `completed`: Funds returned to client
 * - `failed`: Stripe refund failed (e.g., insufficient balance)
 * - `rejected`: Admin rejected refund request
 * 
 * **Fee Handling:**
 * - Platform fees can be refunded proportionally
 * - Vendor payout may need to be reversed if already transferred
 * - Complex scenarios handled through adjustment transactions
 * 
 * **Available Hooks:**
 * 1. `usePaymentRefunds` - Query refunds with filters
 * 2. `useRequestRefund` - Submit new refund request
 * 3. `useProcessRefund` - Admin approve/reject refund
 * 
 * @example
 * // Request a refund
 * const { mutate: requestRefund } = useRequestRefund();
 * 
 * requestRefund({
 *   paymentTransactionId: 'txn-123',
 *   refundType: 'partial',
 *   refundReason: 'Service not as described',
 *   refundAmountCents: 50000, // $500.00
 *   reasonDetails: 'Only half of items delivered'
 * });
 * 
 * @example
 * // View pending refund requests (admin)
 * const { data: refunds } = usePaymentRefunds({ status: 'requested' });
 * 
 * refunds?.map(refund => (
 *   <RefundCard refund={refund}>
 *     <Button onClick={() => processRefund({
 *       refundId: refund.id,
 *       action: 'approve',
 *       adminNotes: 'Valid complaint, approved'
 *     })}>
 *       Approve
 *     </Button>
 *   </RefundCard>
 * ));
 * 
 * @example
 * // Process refund decision
 * const { mutate: processRefund } = useProcessRefund();
 * 
 * processRefund({
 *   refundId: 'ref-456',
 *   action: 'reject',
 *   adminNotes: 'Service completed as specified per contract'
 * });
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

export interface PaymentRefund {
  id: string;
  payment_transaction_id: string;
  stripe_refund_id: string | null;
  refund_type: 'full' | 'partial';
  refund_reason: string;
  refund_amount_cents: number;
  platform_fee_refund_cents: number;
  requested_by_user_id: string | null;
  approved_by_admin_id: string | null;
  refund_status: 'requested' | 'approved' | 'processing' | 'completed' | 'failed' | 'rejected';
  reason_details: string | null;
  admin_notes: string | null;
  requested_at: string;
  approved_at: string | null;
  completed_at: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  payment_transaction?: any;
}

export const usePaymentRefunds = (filters?: { 
  status?: string;
  transactionId?: string;
}) => {
  return useQuery({
    queryKey: ['payment-refunds', filters],
    queryFn: async () => {
      let query = supabase
        .from('payment_refunds')
        .select('*, payment_transaction:payment_transactions(*)')
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('refund_status', filters.status);
      }
      if (filters?.transactionId) {
        query = query.eq('payment_transaction_id', filters.transactionId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as PaymentRefund[];
    },
  });
};

export const useRequestRefund = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      paymentTransactionId: string;
      refundType: 'full' | 'partial';
      refundReason: string;
      refundAmountCents?: number;
      reasonDetails?: string;
    }) => {
      const { data, error } = await invokeEdgeFunction('request-payment-refund', {
        body: params,
        callerOperation: 'useRequestRefund.mutate',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-refunds'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
      toast({
        title: 'Refund Requested',
        description: 'Your refund request has been submitted for admin review.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to request refund',
        variant: 'destructive',
      });
    },
  });
};

export const useProcessRefund = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      refundId: string;
      action: 'approve' | 'reject';
      adminNotes?: string;
    }) => {
      const { data, error } = await invokeEdgeFunction('process-payment-refund', {
        body: params,
        callerOperation: 'useProcessRefund.mutate',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payment-refunds'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
      toast({
        title: variables.action === 'approve' ? 'Refund Approved' : 'Refund Rejected',
        description: variables.action === 'approve' 
          ? 'The refund has been processed successfully.'
          : 'The refund request has been rejected.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to process refund',
        variant: 'destructive',
      });
    },
  });
};
